import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

/**
 * Route Navigation Path
 * Animates a glowing line from start point to destination booth
 */
export function RouteNavigationPath({ start, end, active }) {
  const lineRef = useRef();
  const arrowRef = useRef();
  const progressRef = useRef(0);
  
  const points = useMemo(() => {
    if (!start || !end) return [];
    
    // Create a path with waypoints (Manhattan-style with slight arc)
    const startVec = new THREE.Vector3(start[0], 0.5, start[2]);
    const endVec = new THREE.Vector3(end[0], 0.5, end[2]);
    
    // Waypoint for cleaner path - go horizontally then vertically
    const mid1 = new THREE.Vector3(start[0], 0.5, (start[2] + end[2]) / 2);
    const mid2 = new THREE.Vector3(end[0], 0.5, (start[2] + end[2]) / 2);
    
    // Interpolate points along path for smooth animation
    const pathPoints = [];
    const segments = 60;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      let p;
      if (t < 0.33) {
        const localT = t / 0.33;
        p = new THREE.Vector3().lerpVectors(startVec, mid1, localT);
      } else if (t < 0.66) {
        const localT = (t - 0.33) / 0.33;
        p = new THREE.Vector3().lerpVectors(mid1, mid2, localT);
      } else {
        const localT = (t - 0.66) / 0.34;
        p = new THREE.Vector3().lerpVectors(mid2, endVec, localT);
      }
      // Add slight arc (higher in middle)
      p.y = 0.5 + Math.sin(t * Math.PI) * 1.5;
      pathPoints.push(p);
    }
    
    return pathPoints;
  }, [start, end]);
  
  const geometry = useMemo(() => {
    if (points.length === 0) return null;
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    return geo;
  }, [points]);
  
  // Animate the moving indicator along the path
  useFrame((state, delta) => {
    if (!active || points.length === 0) return;
    
    progressRef.current = (progressRef.current + delta * 0.15) % 1;
    const idx = Math.floor(progressRef.current * (points.length - 1));
    const pt = points[idx];
    
    if (arrowRef.current && pt) {
      arrowRef.current.position.set(pt.x, pt.y + 0.5, pt.z);
      arrowRef.current.rotation.y = state.clock.elapsedTime * 3;
    }
  });
  
  if (!active || !geometry || !end) return null;
  
  // Calculate distance
  const distance = start && end ? Math.sqrt(
    Math.pow(end[0] - start[0], 2) + Math.pow(end[2] - start[2], 2)
  ) : 0;
  const walkingTime = Math.max(1, Math.round(distance / 5)); // 5m per minute walking
  
  return (
    <group>
      {/* Path line */}
      <line ref={lineRef} geometry={geometry}>
        <lineBasicMaterial 
          color="#f59e0b" 
          linewidth={3}
          transparent
          opacity={0.9}
        />
      </line>
      
      {/* Glowing dots along the path */}
      {points.filter((_, i) => i % 3 === 0).map((p, i) => (
        <mesh key={i} position={[p.x, p.y, p.z]}>
          <sphereGeometry args={[0.15, 8, 8]} />
          <meshBasicMaterial color="#fbbf24" transparent opacity={0.7} />
        </mesh>
      ))}
      
      {/* Animated arrow indicator */}
      <group ref={arrowRef}>
        <mesh>
          <coneGeometry args={[0.6, 1.2, 4]} />
          <meshStandardMaterial 
            color="#f97316" 
            emissive="#f97316" 
            emissiveIntensity={0.8}
          />
        </mesh>
      </group>
      
      {/* Start marker */}
      {start && (
        <group position={[start[0], 0, start[2]]}>
          <mesh position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
            <ringGeometry args={[1.5, 2, 32]} />
            <meshBasicMaterial color="#22c55e" transparent opacity={0.8} />
          </mesh>
          <mesh position={[0, 3, 0]} castShadow>
            <coneGeometry args={[0.8, 2, 8]} />
            <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.5} />
          </mesh>
          <Html zIndexRange={[10, 0]} position={[0, 5, 0]} center>
            <div className="bg-green-500 text-white px-3 py-1.5 rounded-lg shadow-2xl text-xs font-bold whitespace-nowrap pointer-events-none">
              📍 START
            </div>
          </Html>
        </group>
      )}
      
      {/* End marker with info */}
      {end && (
        <group position={[end[0], 0, end[2]]}>
          <mesh position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
            <ringGeometry args={[1.8, 2.4, 32]} />
            <meshBasicMaterial color="#ef4444" transparent opacity={0.8} />
          </mesh>
          <Html zIndexRange={[10, 0]} position={[0, 8, 0]} center>
            <div className="bg-red-500 text-white px-4 py-2 rounded-lg shadow-2xl text-xs font-bold whitespace-nowrap pointer-events-none border-2 border-white">
              🎯 DESTINATION<br />
              <span className="text-[10px] opacity-90">~{walkingTime} min walk • {Math.round(distance)}m</span>
            </div>
          </Html>
        </group>
      )}
    </group>
  );
}
